/* eslint-disable no-redeclare, @typescript-eslint/no-redeclare */
import { FC, useEffect, useState } from 'react';
import { Box, Group, Paper, Skeleton, Stack, Text } from '@mantine/core';
import { RoutingS } from '../../Service/RoutingS';
import ResourcePlanningS from '../../Service/restapi/resourcePlanningS';
import { Planning, PlanningGroup, ResourceType } from '../../Types/ResourceT';
import { ThemeText } from '../../Atoms/ThemeText';

type ResourcePreview = {
  resourceId: string;
  resourceName: string;
  quantity: number;
  resourceType?: ResourceType;
};

type PlanningGroupPreview = {
  id: string;
  description: string;
  resources: ResourcePreview[];
};

const flattenPlannings = (plannings: Planning[]): Planning[] => {
  const items: Planning[] = [];
  const walk = (nodes: Planning[]) => {
    nodes.forEach((planning) => {
      items.push(planning);
      if (planning.children && planning.children.length > 0) {
        walk(planning.children);
      }
    });
  };
  walk(plannings);
  return items;
};

const getResourceTypeEmoji = (type?: ResourceType) => {
  if (type === ResourceType.ITEM) {
    return '📦';
  }
  if (type === ResourceType.CASE) {
    return '🧳';
  }
  if (type === ResourceType.KIT) {
    return '🧰';
  }
  return '📁';
};

const openResourceDetails = (resourceId: string) => {
  RoutingS.openInNewTab(`/resources/${resourceId}/details`);
};

const EventResourcePlanningPreview: FC<{ eventId: number; maxWidth: number }> = ({ eventId, maxWidth: _maxWidth }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<PlanningGroupPreview[]>([]);

  useEffect(() => {
    let active = true;
    setLoading(true);

    const fetch = async () => {
      const planningGroups = await ResourcePlanningS.getPlanningGroupsByEvent(`${eventId}`);
      if (!active) {
        return;
      }
      if (!planningGroups || planningGroups.length === 0) {
        setGroups([]);
        return;
      }

      const previews = await Promise.all(
        planningGroups.map(async (group: PlanningGroup) => {
          const plannings = await ResourcePlanningS.fetchPlannings(group.id);
          const flatPlannings = flattenPlannings(plannings);

          const resourcesMap: Record<
            string,
            { resourceId: string; resourceName: string; quantity: number; resourceType?: ResourceType }
          > = {};
          flatPlannings.forEach((planning) => {
            const resourceId = planning.resource?.id;
            const resourceName = planning.resource?.name;
            const resourceType = planning.resource?.type;
            if (!resourceId || !resourceName) {
              return;
            }

            const existing = resourcesMap[resourceId];
            if (existing) {
              existing.quantity += planning.plannedQuantity ?? 0;
            } else {
              resourcesMap[resourceId] = {
                resourceId,
                resourceName,
                quantity: planning.plannedQuantity ?? 0,
                resourceType,
              };
            }
          });

          const resourcePreviews = Object.values(resourcesMap).sort((a, b) =>
            a.resourceName.localeCompare(b.resourceName),
          );

          return {
            id: group.id,
            description: group.description,
            resources: resourcePreviews,
          };
        }),
      );

      if (active) {
        setGroups(previews.filter((group) => group.resources.length > 0));
      }
    };

    fetch().finally(() => {
      if (active) {
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [eventId]);

  if (loading) {
    return <Skeleton mt={6} height={40} width={220} />;
  }

  if (groups.length === 0) {
    return null;
  }

  const totalResources = groups.reduce((sum, group) => sum + group.resources.length, 0);

  return (
    <>
      <Box
        p="xs"
        my="xs"
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[0],
          borderRadius: 10,
        })}
      >
        <Group>
          <ThemeText weight="bold">Planungsvorschau</ThemeText>
          {groups.length > 1 && (
            <Text size={10} color="dimmed">
              {' '}
              {`${groups.length} Gruppen · ${totalResources} Ressourcen`}
            </Text>
          )}
        </Group>
      </Box>

      <Stack spacing={8}>
        {groups.map((group) => {
          const visibleResources = group.resources.slice(0, 5);
          const hiddenResources = group.resources.length - visibleResources.length;
          return (
            <Paper key={group.id} className="event-list-item" shadow="xs" radius={10} p="xs">
              <Text className="overflow-ellipsis">{group.description}</Text>
              <Group mt={8} spacing={6}>
                {visibleResources.map((resource) => (
                  <Text
                    key={resource.resourceId}
                    size="xs"
                    weight="bolder"
                    onClick={() => openResourceDetails(resource.resourceId)}
                    sx={(theme) => ({
                      padding: '3px 8px',
                      borderRadius: theme.radius.sm,
                      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
                      cursor: 'pointer',
                    })}
                    className="overflow-ellipsis"
                  >
                    {`${getResourceTypeEmoji(resource.resourceType)}  ${resource.resourceName} `}
                    <Text span weight={400}>
                      {`(${resource.quantity})`}
                    </Text>
                  </Text>
                ))}
                {hiddenResources > 0 && (
                  <Text size="xs" color="dimmed" weight={600}>
                    {`+${hiddenResources}`}
                  </Text>
                )}
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </>
  );
};

export default EventResourcePlanningPreview;
